import { doc, getDoc, setDoc, updateDoc, serverTimestamp, collection, addDoc, getDocs, deleteDoc, query, orderBy } from 'firebase/firestore';
import { db } from '../../firebase';

export interface PasswordManagerData {
  pin?: string;
  note?: string;
  updatedAt?: any;
}

const COLLECTION = 'password_manager';
// Single shared vault for the whole team. The vault used to be keyed by the
// signed-in user's uid, which meant each person saw only their own entries.
// Everything now lives under this one doc so both app users share it.
export const SHARED_VAULT_ID = 'shared';
const DEFAULT_PIN = '1234';

export type ThemeVariant = 'primary' | 'success' | 'warning' | 'info' | 'danger' | 'secondary';

export interface CredentialEntry {
  id?: string;
  title: string;
  username: string;
  password: string;
  notes?: string;
  // New styling using Metronic/Bootstrap variants. Default: 'primary'.
  variant?: ThemeVariant;
  createdAt?: any;
  updatedAt?: any;
}

const vaultRef = () => doc(db, COLLECTION, SHARED_VAULT_ID);

export const ensureDoc = async (): Promise<void> => {
  const ref = vaultRef();
  const snap = await getDoc(ref);
  if (!snap.exists()) {
    await setDoc(ref, {
      pin: DEFAULT_PIN,
      note: '',
      updatedAt: serverTimestamp(),
    });
  }
};

export const getPasswordManagerData = async (): Promise<PasswordManagerData> => {
  const ref = vaultRef();
  const snap = await getDoc(ref);
  if (!snap.exists()) {
    await ensureDoc();
    return { pin: DEFAULT_PIN, note: '' };
  }
  return snap.data() as PasswordManagerData;
};

export const saveNote = async (note: string): Promise<void> => {
  await setDoc(vaultRef(), { note, updatedAt: serverTimestamp() }, { merge: true });
};

export const getPin = async (): Promise<string> => {
  const data = await getPasswordManagerData();
  return (data.pin && data.pin.length > 0 ? data.pin : DEFAULT_PIN);
};

export const setPin = async (pin: string): Promise<void> => {
  await setDoc(vaultRef(), { pin, updatedAt: serverTimestamp() }, { merge: true });
};

// Credentials subcollection API
const entriesCollection = (vaultId: string = SHARED_VAULT_ID) => collection(doc(db, COLLECTION, vaultId), 'entries');

export const listEntries = async (): Promise<CredentialEntry[]> => {
  const q = query(entriesCollection(), orderBy('updatedAt', 'desc'));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...(d.data() as Omit<CredentialEntry, 'id'>) }));
};

export const addEntry = async (entry: Omit<CredentialEntry, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> => {
  const now = serverTimestamp();
  const payload: any = { title: entry.title, username: entry.username, password: entry.password, notes: entry.notes || '', createdAt: now, updatedAt: now };
  if (entry.variant) payload.variant = entry.variant; else payload.variant = 'primary';
  const res = await addDoc(entriesCollection(), payload);
  return res.id;
};

export const updateEntry = async (entryId: string, entry: Partial<CredentialEntry>): Promise<void> => {
  const ref = doc(entriesCollection(), entryId);
  const patch: any = { ...entry };
  Object.keys(patch).forEach((k) => { if (patch[k] === undefined) delete patch[k]; });
  patch.updatedAt = serverTimestamp();
  await updateDoc(ref, patch);
};

export const deleteEntry = async (entryId: string): Promise<void> => {
  const ref = doc(entriesCollection(), entryId);
  await deleteDoc(ref);
};

// One-time lift of a legacy per-user vault into the shared one. Safety net for
// anyone whose entries were still sitting under their own uid when the vault
// went shared (the bulk was migrated server-side). No-op once the shared vault
// has content, so it can never duplicate entries.
export const migrateLegacyVaultIfAny = async (userId: string): Promise<boolean> => {
  if (!userId || userId === SHARED_VAULT_ID) return false;
  const sharedSnap = await getDocs(entriesCollection());
  if (!sharedSnap.empty) return false;

  const legacySnap = await getDocs(entriesCollection(userId));
  if (legacySnap.empty) return false;

  for (const d of legacySnap.docs) {
    const data = d.data() as Omit<CredentialEntry, 'id'>;
    await setDoc(doc(entriesCollection(), d.id), {
      title: data.title || '',
      username: data.username || '',
      password: data.password || '',
      notes: data.notes || '',
      variant: data.variant || 'primary',
      createdAt: data.createdAt || serverTimestamp(),
      updatedAt: data.updatedAt || serverTimestamp(),
    });
  }

  // Carry the legacy PIN across so the vault still unlocks with the same code.
  const legacyDoc = await getDoc(doc(db, COLLECTION, userId));
  const legacyPin = legacyDoc.exists() ? (legacyDoc.data() as PasswordManagerData).pin : undefined;
  if (legacyPin) {
    const shared = await getDoc(vaultRef());
    if (!shared.exists() || !(shared.data() as PasswordManagerData).pin) {
      await setDoc(vaultRef(), { pin: legacyPin, updatedAt: serverTimestamp() }, { merge: true });
    }
  }
  return true;
};

// Optional migration: move single note to an entry once
export const migrateSingleNoteIfAny = async (userId: string): Promise<void> => {
  await migrateLegacyVaultIfAny(userId);

  const pmRef = vaultRef();
  const pmSnap = await getDoc(pmRef);
  const entriesSnap = await getDocs(entriesCollection());
  if (pmSnap.exists() && entriesSnap.empty) {
    const data = pmSnap.data() as PasswordManagerData;
    const note = data.note || '';
    if (note.trim().length > 0) {
      await addEntry({
        title: 'Imported Note',
        username: '',
        password: '',
        notes: note,
        variant: 'primary',
      });
      // Clear original note to avoid repeated imports
      await setDoc(pmRef, { note: '' }, { merge: true });
    }
  }
};
